import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Link as MuiLink, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { ReferralWithScope_UserWithoutId, CacheKeys } from '../../helpers/DataStructures';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import { isArchivedInDashboardPreview } from '../../helpers/referralArchive';

import { translateFromStatusToShortTextForOfferer } from '../../helpers/Language';
import { getReferralUnreadCount } from '../../helpers/referralUnread';

import { InHouseCardHeader, InHouseCardContent } from '../../components/CustomMUI';
import ReferralUnreadBadge from './ReferralUnreadBadge';
import Busy from '../../components/Busy';

const ReferralsAppReferralsIn: React.FC = () => {

  const apiServiceInstance = useAPIAuthenticatedService();  
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backTo = `${location.pathname}${location.search}${location.hash}`;

  const { isLoading, data } = useQuery(
    [CacheKeys.REFERRALS_TO_USER],
    () => apiServiceInstance.getUserReferralsIn(),
    { keepPreviousData: true }
  );

  // REQ-054: dashboard previews should hide stalled completed states over a week old; full routes stay unfiltered.
  const visibleReferrals = React.useMemo(() => {
    if (data === undefined) {
      return [];
    }

    return data.filter((referral) => !isArchivedInDashboardPreview(referral));
  }, [data]);

  return (

    <>
    
      <Card variant="outlined" title='dd' style={{ margin: '0 auto' }}>
       
              <InHouseCardHeader
                title={
                  <Typography
                    variant='h3'
                    color='grey.50'
                    sx={{ textAlign: 'center', width: '100%' }}
                  >
                    Live Referrals Received -{' '}
                    <MuiLink component={RouterLink} to="/dashboard/referrals/in">
                      View All
                    </MuiLink>
                  </Typography>
                }
              />
              <InHouseCardContent style={{ textAlign: 'center' }} > 

              { 
                isLoading || data === undefined  ? 
                (
                    // REQ-034: keep dashboard referral previews free of debug placeholder text.
                    <Busy message="Loading referrals..." />
                ) : visibleReferrals.length === 0 ?
                (
                    <Typography variant='body2'>No active referrals received yet</Typography>
                ) :
                (
                    <TableContainer component={Paper}>
                    <Table>
                        <TableBody>
                          {visibleReferrals.map((referral: ReferralWithScope_UserWithoutId) => (
                            <React.Fragment key={referral.id}>
                              <TableRow
                                onClick={() =>
                                  navigate(`/dashboard/referrals/in/${referral.id}`, {
                                    state: { backTo },
                                  })
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <TableCell>
                                  {referral.user.givenName} {referral.user.familyName}
                                </TableCell>
                                <TableCell>{DateTimeFormat(referral.created)}</TableCell>
                                <TableCell>{referral.theReferralName}</TableCell>
                                <TableCell>
                                  <Stack spacing={0.75} alignItems="flex-start">
                                    <ReferralUnreadBadge count={getReferralUnreadCount(referral)} />
                                    <Typography component="div" variant="inherit">
                                      {translateFromStatusToShortTextForOfferer(referral.status)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                {!isMobile && (
                                  <TableCell>
                                    <Button
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/dashboard/referrals/in/${referral.id}`, {
                                          state: { backTo },
                                        });
                                      }}
                                    >
                                      View
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>

                    </Table>
                    </TableContainer>
                )
            }

              </InHouseCardContent>               
                          
      </Card>

        </>
    
  );
};

export default ReferralsAppReferralsIn;
